"use client";

import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import styles from "./CarImageGalleryWrapper.module.scss";
import Button from "@components/atoms/Button";
import Image from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";
import WishlistButton from "@components/atoms/WishlistButton";
import WishlistToast from "@components/atoms/WishlistToast";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { useWishlistToast } from "@hooks/useWishlistToast";
import { useShortlist } from "@hooks/useShortlist";

interface CarImageGalleryWrapperProps {
  children: React.ReactNode[];
  className?: string;
  slug?: string;
  brandName?: string;
  modelName?: string;
  isLoggedIn?: boolean;
  cityName?: string;
  isInApp?: boolean;
}

export const CarImageGalleryWrapper = ({
  children,
  className = "",
  slug,
  brandName,
  modelName,
  isLoggedIn = false,
  cityName,
  isInApp,
}: CarImageGalleryWrapperProps) => {
  const router = useRouter();
  const hasBottomNav = useHasBottomNav();
  const childItems = Array.isArray(children) ? children : [children];
  const [currentIndex, setCurrentIndex] = useState(0);
  const { toast, showToast, hideToast } = useWishlistToast();
  const { isCarShortlisted, toggleShortlistItem } = useShortlist();
  const mobileScrollRef = useRef<HTMLDivElement | null>(null);

  const showWishlistBtn = slug && brandName && modelName;
  const isWishlisted = slug ? isCarShortlisted(slug) : false;

  const handleWishlistClick = async () => {
    if (!slug || !brandName || !modelName) return;
    const car = { id: slug, make: brandName, name: modelName };
    const result = await toggleShortlistItem(car);
    if (result.success) {
      showToast(car, !isWishlisted);
    }
  };

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === childItems.length - 1;

  const next = () => {
    if (!isLast) setCurrentIndex((prev) => prev + 1);
  };

  const prev = () => {
    if (!isFirst) setCurrentIndex((prev) => prev - 1);
  };

  const scrollToIndex = (index: number) => {
    const container = mobileScrollRef.current;
    if (!container) return;

    const card = container.children[index] as HTMLElement | null;
    if (!card) return;

    container.scrollTo({
      left:
        card.offsetLeft - (container.clientWidth - card.offsetWidth) / 2,
      behavior: "smooth",
    });
  };

  const goTo = (index: number) => {
    setCurrentIndex(index);
    scrollToIndex(index);
  };

  const handleMobileScroll = () => {
    const container = mobileScrollRef.current;
    if (!container) return;

    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let boundedIndex = currentIndex;
    let closestDistance = Number.POSITIVE_INFINITY;

    Array.from(container.children).forEach((child, index) => {
      const card = child as HTMLElement;
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(containerCenter - cardCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        boundedIndex = index;
      }
    });

    if (boundedIndex !== currentIndex) {
      setCurrentIndex(boundedIndex);
    }
  };

  return (
    <>
      <div className={className}>
        {/* Mobile */}
        <div className={styles.mobileLayout}>
          <div
            className={styles.mobileImageSection}
            ref={mobileScrollRef}
            onScroll={handleMobileScroll}
          >
            {childItems?.map((child, index) => (
              <div className={styles.mobileCard} key={index}>
                {child}
              </div>
            ))}
          </div>
          <div className={styles.mobileNavigation}>
            {childItems?.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={
                  i === currentIndex
                    ? `${styles.navBar} ${styles.navBarActive}`
                    : styles.navBar
                }
              />
            ))}
          </div>
          {!isInApp && showWishlistBtn && (
            <WishlistButton
              isLoggedIn={isLoggedIn}
              isInWishlist={isWishlisted}
              onWishlistClick={handleWishlistClick}
              size="small"
              className={styles.wishlistButton}
              model_name={modelName}
              brand_name={brandName}
              cityName={cityName}
            />
          )}
        </div>

        {/* Desktop */}
        <div className={styles.desktopLayout}>
          <div className={styles.desktopImageSection}>
            {childItems?.[currentIndex]}
          </div>
          <div className={styles.desktopNavigation}>
            <span className={styles.imageCounter}>
              {`${currentIndex + 1}/${childItems?.length}`}
            </span>
            <Button
              onClick={prev}
              disabled={isFirst}
              className={styles.navButton}
              size="ds"
              aria-disabled={isFirst}
            >
              <Image
                src={`${assetLoader()}/icons/HeroSection/ArrowLeft.svg`}
                alt="Left Arrow"
                className={styles.navIcon}
                width={20}
                height={20}
                loading="lazy"
              />
            </Button>
            <Button
              onClick={next}
              disabled={isLast}
              className={styles.navButton}
              size="ds"
              aria-disabled={isLast}
            >
              <Image
                src={`${assetLoader()}/icons/HeroSection/ArrowRight.svg`}
                alt="Right Arrow"
                className={styles.navIcon}
                width={20}
                height={20}
                loading="lazy"
              />
            </Button>
          </div>
        </div>
      </div>

      {!isInApp && (
        <WishlistToast
          isVisible={toast.show}
          isAdding={toast.isAdding}
          carMake={toast.carMake}
          carName={toast.carName}
          hasBottomNav={hasBottomNav}
          onViewWishlist={() => {
            hideToast();
            router.push("/wishlist");
          }}
          onUndo={async () => {
            if (!slug || !brandName || !modelName) return hideToast();
            const car = { id: slug, make: brandName, name: modelName };
            await toggleShortlistItem(car);
            hideToast();
          }}
        />
      )}
    </>
  );
};
